import { UnpaidInvoice } from 'types/Insight';

/**
 * Extracts clean token from invoice link (removes query params)
 */
export const extractTokenFromInvoiceLink = (link: string): string => {
  const parts = link.split('/');
  const lastPart = parts[parts.length - 1] || '';
  return lastPart.split('?')[0]; // Remove query parameters like ?locale=EN_US
};

/**
 * Extracts zip code from payment details, checking multiple possible locations
 */
export const extractZipCode = (paymentDetails: any): string => {
  if (!paymentDetails) return '';

  // Check direct properties first
  if (paymentDetails.zipCode) return paymentDetails.zipCode;
  if (paymentDetails.postalCode) return paymentDetails.postalCode;

  // Check address object
  if (paymentDetails.address) {
    if (paymentDetails.address.zipCode) return paymentDetails.address.zipCode;
    if (paymentDetails.address.postalCode) return paymentDetails.address.postalCode;
    if (paymentDetails.address.zip) return paymentDetails.address.zip;

    // Check nested addressComponents structure
    if (
      paymentDetails.address.addressComponents &&
      paymentDetails.address.addressComponents[0] &&
      paymentDetails.address.addressComponents[0].name === 'postalCode'
    ) {
      return paymentDetails.address.addressComponents[0].value;
    }
  }

  // Check billingAddress
  if (paymentDetails.billingAddress) {
    if (paymentDetails.billingAddress.zipCode) return paymentDetails.billingAddress.zipCode;
    if (paymentDetails.billingAddress.postalCode) return paymentDetails.billingAddress.postalCode;
    if (paymentDetails.billingAddress.zip) return paymentDetails.billingAddress.zip;
  }

  return '';
};

/**
 * Converts unpaid invoices to batch sales info format
 */
export const createBatchSalesInfo = (unpaidInvoices: any[], currency: string) => {
  return unpaidInvoices.map((invoice: any) => ({
    token: extractTokenFromInvoiceLink(invoice.link),
    amount: parseFloat(invoice.balance.toString()).toFixed(2),
    currency: currency || 'USD',
  }));
};

/**
 * Calculates total amount from batch sales info
 */
export const calculateTotalAmount = (batchSalesInfo: any[]): string => {
  return batchSalesInfo
    .reduce((sum: number, item: any) => sum + parseFloat(item.amount), 0)
    .toFixed(2);
};

/**
 * Calculates sum of all original invoice totals for risk/BioCatch API.
 * This is needed for the activityAmountTotal field in the prepayment call.
 *
 * Falls back to balance if totalAmount is not available (backward compatibility).
 *
 * @param unpaidInvoices - Array of unpaid invoices with totalAmount and/or balance
 * @returns Sum of all original invoice totals
 */
export const calculateTotalOriginalAmount = (unpaidInvoices: UnpaidInvoice[]): number => {
  if (!unpaidInvoices || unpaidInvoices.length === 0) {
    return 0;
  }
  return unpaidInvoices.reduce(
    (sum: number, invoice: UnpaidInvoice) => sum + (invoice.totalAmount ?? invoice.balance ?? 0),
    0
  );
};

/**
 * Creates success payload for batch payment
 */
export const createBatchPaymentSuccessPayload = ({
  batchResponse,
  totalAmount,
  maskedPayment,
  cardType,
  ssrtid,
}: {
  batchResponse: any;
  totalAmount: string;
  maskedPayment: string;
  cardType: string;
  ssrtid: string;
}) => {
  return {
    result: {
      data: {
        batchId: batchResponse.batchId,
        amountPaid: parseFloat(totalAmount),
        maskedCardNumber: maskedPayment,
        cardType,
      },
      config: {
        headers: {
          intuit_tid: ssrtid,
        },
      },
    },
  };
};
